import { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Crown, Paperclip, X, ExternalLink, Users, AlertTriangle } from 'lucide-react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Spinner } from '@/shared/components/ui/Spinner';
import { cn } from '@/shared/utils/utils';
import { useUserOptions } from '@/shared/hooks/useUserOptions';
import { useUser } from '@/shared/hooks/useAuth';
import { PLATFORM_OPTIONS, DURATION_OPTIONS } from '../constants';
import { uploadMeetingFile } from '../hooks/useMeetings';
import type { MeetingPlatform, MeetingAttachment, ScheduleMeetingPayload, BusyBlock } from '../types';

interface ScheduleMeetingModalProps {
  open: boolean;
  date: string;                 // prefilled YYYY-MM-DD
  ceo: { id: string; name: string } | null;
  ceoBusy: BusyBlock[];
  loading?: boolean;
  onClose: () => void;
  onSubmit: (payload: ScheduleMeetingPayload) => void;
}

export function ScheduleMeetingModal({ open, date, ceo, ceoBusy, loading, onClose, onSubmit }: ScheduleMeetingModalProps) {
  const me = useUser();
  const { data: users = [], isLoading: usersLoading } = useUserOptions(open);

  const [title, setTitle] = useState('');
  const [meetingDate, setMeetingDate] = useState(date);
  const [time, setTime] = useState('10:00');
  const [duration, setDuration] = useState('30');
  const [platform, setPlatform] = useState<MeetingPlatform>('google_meet');
  const [link, setLink] = useState('');
  const [note, setNote] = useState('');
  const [attendees, setAttendees] = useState<string[]>([]);
  const [includeCeo, setIncludeCeo] = useState(false);
  const [attachments, setAttachments] = useState<MeetingAttachment[]>([]);
  const [uploading, setUploading] = useState(false);

  // Reset the form whenever the modal opens or the prefilled date changes,
  // adjusting state during render (via previous-value tracking) instead of in an
  // effect.
  const [prevOpen, setPrevOpen] = useState(open);
  const [prevDate, setPrevDate] = useState(date);
  if (open !== prevOpen || date !== prevDate) {
    setPrevOpen(open);
    setPrevDate(date);
    if (open) {
      setTitle(''); setMeetingDate(date); setTime('10:00'); setDuration('30');
      setPlatform('google_meet'); setLink(''); setNote('');
      setAttendees([]); setIncludeCeo(false); setAttachments([]);
    }
  }

  // Assignable people (GET /api/users already excludes the Super Admin).
  const people = useMemo(() => users.filter((u) => u.id !== me?.id), [users, me?.id]);
  const provider = PLATFORM_OPTIONS.find((p) => p.value === platform);

  // Does the CEO have a busy block overlapping the chosen start time?
  const ceoConflict = useMemo(() => {
    if (!includeCeo || !meetingDate || !time) return false;
    const start = new Date(`${meetingDate}T${time}:00Z`).getTime();
    const end = start + (Number(duration) || 30) * 60000;
    return ceoBusy.some((b) => {
      const bs = new Date(b.start).getTime();
      const be = new Date(b.end).getTime();
      return start < be && end > bs;
    });
  }, [includeCeo, meetingDate, time, duration, ceoBusy]);

  const toggleAttendee = (id: string) =>
    setAttendees((l) => (l.includes(id) ? l.filter((x) => x !== id) : [...l, id]));

  const onFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = await Promise.all(Array.from(files).map((f) => uploadMeetingFile(f)));
      setAttachments((prev) => [...prev, ...uploaded]);
    } catch {
      toast.error('File upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    const finalAttendees = [...attendees];
    if (includeCeo && ceo?.id && !finalAttendees.includes(ceo.id)) finalAttendees.push(ceo.id);
    onSubmit({
      title: title.trim(),
      meeting_date: meetingDate,
      meeting_time: time,
      duration_min: Number(duration) || 30,
      platform,
      meeting_link: link.trim() || undefined,
      note: note.trim() || undefined,
      attendees: finalAttendees,
      attachments,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Schedule Meeting"
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button type="submit" form="schedule-form" loading={loading} disabled={uploading}>Schedule</Button>
        </>
      }
    >
      <form id="schedule-form" onSubmit={submit} className="space-y-4">
        <Input label="Title *" name="mtg-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Meeting title…" required />

        <div className="grid grid-cols-3 gap-3">
          <Input label="Date *" name="mtg-date" type="date" value={meetingDate} onChange={(e) => setMeetingDate(e.target.value)} required />
          <Input label="Time" name="mtg-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
          <Select label="Duration" name="mtg-duration" value={duration} onChange={(e) => setDuration(e.target.value)} options={DURATION_OPTIONS} />
        </div>

        {/* Provider + link */}
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Meeting platform"
            name="mtg-platform"
            value={platform}
            onChange={(e) => setPlatform(e.target.value as MeetingPlatform)}
            options={PLATFORM_OPTIONS.map((p) => ({ value: p.value, label: p.label }))}
          />
          <div>
            <label htmlFor="mtg-link" className="mb-1.5 block text-body-sm font-medium text-on-surface">Meeting link</label>
            <div className="flex items-center gap-1.5">
              <Input name="mtg-link" id="mtg-link" type="url" value={link} onChange={(e) => setLink(e.target.value)} placeholder={provider?.hint ?? 'https://…'} disabled={platform === 'in_person'} />
              {provider?.newUrl && (
                <a href={provider.newUrl} target="_blank" rel="noreferrer noopener" title={`Create a new ${provider.label} link`} className="inline-flex h-9 shrink-0 items-center gap-1 rounded border border-outline px-2 text-body-sm text-primary hover:bg-surface-variant">
                  <ExternalLink className="h-3.5 w-3.5" /> New
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Attendees */}
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <label className="flex items-center gap-1.5 text-body-sm font-medium text-on-surface"><Users className="h-4 w-4" /> Attendees</label>
            {ceo && (
              <button
                type="button"
                onClick={() => setIncludeCeo((v) => !v)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-body-sm transition-colors',
                  includeCeo ? 'border-warning bg-warning/10 text-warning-text' : 'border-outline text-on-surface-variant hover:bg-surface-variant',
                )}
                aria-pressed={includeCeo}
              >
                <Crown className="h-3.5 w-3.5" /> {includeCeo ? 'CEO included' : 'Include CEO'}
              </button>
            )}
          </div>
          {usersLoading ? (
            <div className="flex justify-center py-3"><Spinner /></div>
          ) : (
            <div className="max-h-40 space-y-1 overflow-y-auto rounded-card-sm border border-outline p-2">
              {people.length === 0 ? (
                <p className="px-2 py-2 text-body-sm text-on-surface-variant">No other users available.</p>
              ) : people.map((u) => (
                <label key={u.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-surface-variant">
                  <input type="checkbox" checked={attendees.includes(u.id)} onChange={() => toggleAttendee(u.id)} className="accent-primary" />
                  <span className="truncate text-data-md text-on-surface">{u.display_name}</span>
                  <span className="ml-auto text-data-xs uppercase text-on-surface-variant">{u.department}</span>
                </label>
              ))}
            </div>
          )}
          {includeCeo && ceoConflict && (
            <p className="mt-1.5 inline-flex items-center gap-1.5 text-body-sm text-warning">
              <AlertTriangle className="h-3.5 w-3.5" /> {ceo?.name} is already busy at this time — pick a free slot.
            </p>
          )}
        </div>

        {/* Note */}
        <div>
          <label htmlFor="mtg-note" className="mb-1.5 block text-body-sm font-medium text-on-surface">Note / agenda</label>
          <textarea
            id="mtg-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Agenda, context, talking points…"
            className="w-full resize-none rounded border border-outline bg-surface px-3 py-2 text-data-md text-on-surface placeholder:text-on-surface-variant/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>

        {/* Attachments */}
        <div>
          <label className="mb-1.5 flex items-center gap-1.5 text-body-sm font-medium text-on-surface"><Paperclip className="h-4 w-4" /> Attachments</label>
          <input type="file" multiple onChange={(e) => onFiles(e.target.files)} className="block w-full text-body-sm text-on-surface-variant file:mr-3 file:rounded file:border-0 file:bg-surface-variant file:px-3 file:py-1.5 file:text-body-sm file:text-on-surface hover:file:bg-outline/40" />
          {uploading && <p className="mt-1 text-body-sm text-on-surface-variant">Uploading…</p>}
          {attachments.length > 0 && (
            <ul className="mt-2 space-y-1">
              {attachments.map((a, i) => (
                <li key={`${a.url}-${i}`} className="flex items-center justify-between gap-2 rounded-card-sm border border-outline px-2.5 py-1.5 text-body-sm">
                  <span className="truncate text-on-surface">{a.name}</span>
                  <button type="button" onClick={() => setAttachments((prev) => prev.filter((_, idx) => idx !== i))} className="text-on-surface-variant hover:text-danger" aria-label="Remove attachment">
                    <X className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </form>
    </Modal>
  );
}
